import axios from "axios";
import { tokenManager } from "../utils/tokenManager";
// requestScope.js is plain JS, so this import is implicitly typed `any`.
import { getActiveProjectId } from "../utils/requestScope";

const RAG_API_URL =
  window.env?.REACT_APP_RAG_API_URL || "http://localhost:8002/api/v1";

const ragApi = axios.create({
  baseURL: RAG_API_URL,
});

// Listing and creating connections are project-scoped: the API reads
// X-Project-ID to decide which project a connection belongs to and which ones
// come back. Both are the collection endpoint — GET lists, POST creates.
const MCP_CONNECTIONS_COLLECTION = "/agent-tools/mcp-connections/";

// Registered before the token manager's interceptors so it runs last — axios
// executes request interceptors in reverse registration order — and its header
// therefore survives the token manager's scope-header reset. Setting the
// header at the call site would not: the token manager deletes X-Project-ID
// from every outgoing request before re-applying the active scope.
ragApi.interceptors.request.use((config) => {
  const projectId: string | null = getActiveProjectId();
  if (projectId && config.url === MCP_CONNECTIONS_COLLECTION) {
    config.headers["X-Project-ID"] = projectId;
  }
  return config;
});

tokenManager.setupInterceptors(ragApi);

// Normalize a /test response body (from either a 2xx response or an HTTP error
// payload) into the UI-facing result. `error` may be a plain string (legacy)
// or a structured { code, message } object we use to detect re-auth.
const normalizeTestResult = (
  data: MCPConnectionTestResultDTO,
): MCPConnectionTestResult => {
  const ok =
    data.ok ?? data.success ?? (data.status ? data.status === "ok" : false);
  const errorObj =
    data.error && typeof data.error === "object" ? data.error : null;
  const errorCode = errorObj?.code ?? null;
  const errorMessage =
    errorObj?.message ??
    (typeof data.error === "string" ? data.error : null) ??
    data.message ??
    null;
  return {
    ok,
    error: ok ? null : errorMessage,
    errorCode,
    authRequired: errorCode === "MCP_AUTH_ERROR",
    latency_ms: data.latency_ms ?? null,
  };
};

export type MCPTransport = "http" | "sse" | "websocket" | "stdio";
// The API's own spelling — snake_case, not the hyphenated form.
export type MCPAuthType = "none" | "oauth" | "api_key" | "bearer";
export type MCPSharingScope = "organization" | "project" | "user";

export interface MCPSharingOrganization {
  scope: "organization";
}

export interface MCPSharingProject {
  scope: "project";
  item: string;
  name?: string | null;
}

export type MCPUserRole = "viewer" | "editor" | "owner";

export interface MCPSharingUserItem {
  id: string;
  role: MCPUserRole;
}

export interface MCPSharingUser {
  scope: "user";
  // Reads may return bare ids (legacy) or {id, role} objects; writes always
  // send {id, role} objects so per-user access levels persist.
  items: Array<string | MCPSharingUserItem>;
}

export type MCPSharing =
  | MCPSharingOrganization
  | MCPSharingProject
  | MCPSharingUser;

// Tools can additionally "inherit" their sharing from the connection.
export interface MCPSharingInherit {
  scope: "inherit";
}

export type MCPToolSharing = MCPSharing | MCPSharingInherit;

export interface MCPConnectionDTO {
  id: string;
  alias?: string | null;
  endpoint: string;
  transport: MCPTransport;
  auth_type: MCPAuthType;
  description: string;
  auth_header_name?: string | null;
  auth_client_id?: string | null;
  oauth_client_mode?: string | null;
  is_active: boolean;
  project_id?: string | null;
  needs_attention: boolean;
  last_connection?: string | null;
  last_error?: string | null;
  icon_url?: string | null;
  created_at: string;
  updated_at: string;
  sharing: MCPSharing;
}

export interface PaginationMetadata {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

export interface PaginatedMCPConnectionsResponse {
  items: MCPConnectionDTO[];
  metadata: PaginationMetadata;
}

export interface ListMCPConnectionsParams {
  page?: number;
  pageSize?: number;
  // Restricts the result to these projects. Omit (or pass an empty list) to
  // let the API decide the scope — that's what "all projects" means.
  projectIds?: string[];
}

export type OAuthClientMode = "dcr" | "manual";
export type OAuthTokenEndpointAuthMethod =
  | "none"
  | "client_secret_basic"
  | "client_secret_post";

export type UpdateMCPConnectionPayload = Partial<CreateMCPConnectionPayload>;

export interface MCPOAuthInit {
  authorization_url: string;
  state: string;
  mcp_connection_id: string;
}

export interface CreateMCPConnectionResponse {
  connection: MCPConnectionDTO;
  oauth_init?: MCPOAuthInit;
}

export interface CreateMCPConnectionPayload {
  alias: string;
  endpoint: string;
  transport: MCPTransport;
  auth_type: MCPAuthType;
  auth_header_name?: string;
  auth_client_id?: string;
  oauth_client_mode?: OAuthClientMode;
  oauth_token_endpoint_auth_method?: OAuthTokenEndpointAuthMethod;
  extra_headers?: Record<string, unknown>;
  is_active?: boolean;
  project_id?: string;
  auth_token?: string;
  auth_client_secret?: string;
  sharing: MCPSharing;
}

export const agentToolsApi = {
  listMCPConnections: async ({
    page = 0,
    pageSize = 20,
    projectIds = [],
  }: ListMCPConnectionsParams = {}): Promise<PaginatedMCPConnectionsResponse> => {
    // project_ids repeats one key per id, matching how the rest of the app
    // filters by project (see rag-service's collection endpoints).
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("page_size", String(pageSize));
    for (const projectId of projectIds) {
      params.append("project_ids", projectId);
    }

    const response = await ragApi.get<PaginatedMCPConnectionsResponse>(
      MCP_CONNECTIONS_COLLECTION,
      { params },
    );
    return response.data;
  },

  getMCPConnection: async (id: string): Promise<MCPConnectionDTO> => {
    const response = await ragApi.get<MCPConnectionDTO>(
      `/agent-tools/mcp-connections/${id}`,
    );
    return response.data;
  },

  createMCPConnection: async (
    payload: CreateMCPConnectionPayload,
  ): Promise<CreateMCPConnectionResponse> => {
    const response = await ragApi.post<
      CreateMCPConnectionResponse | MCPConnectionDTO
    >("/agent-tools/mcp-connections/", payload);
    const data = response.data;
    // OAuth connectors return `{ connection, oauth_init }`; other auth types
    // historically returned the connection directly. Normalize to one shape.
    if (
      data &&
      typeof data === "object" &&
      "connection" in data &&
      data.connection
    ) {
      return data as CreateMCPConnectionResponse;
    }
    return { connection: data as MCPConnectionDTO };
  },

  updateMCPConnection: async (
    id: string,
    payload: UpdateMCPConnectionPayload,
  ): Promise<MCPConnectionDTO> => {
    const response = await ragApi.put<MCPConnectionDTO>(
      `/agent-tools/mcp-connections/${id}`,
      payload,
    );
    return response.data;
  },

  deleteMCPConnection: async (id: string): Promise<void> => {
    await ragApi.delete(`/agent-tools/mcp-connections/${id}`);
  },

  testMCPConnection: async (id: string): Promise<MCPConnectionTestResult> => {
    try {
      const response = await ragApi.post<MCPConnectionTestResultDTO>(
        `/agent-tools/mcp-connections/${id}/test`,
        undefined,
        // MCP_AUTH_ERROR comes back as a 401. Pre-set `_retry` so the global
        // response interceptor treats it as "already retried" and skips the
        // token-refresh-or-logout path — otherwise a tool-level auth error
        // could log the user out of the whole app.
        { _retry: true } as never,
      );
      return normalizeTestResult(response.data ?? {});
    } catch (err) {
      // The backend returns failures as HTTP errors (e.g. 401 for
      // MCP_AUTH_ERROR), so axios rejects. The structured body still lives on
      // error.response.data — parse it instead of treating it as a hard crash.
      const body = (
        err as { response?: { data?: MCPConnectionTestResultDTO } }
      )?.response?.data;
      if (body && typeof body === "object") {
        return normalizeTestResult({ ...body, success: false });
      }
      // No usable body (network failure) — surface a generic offline result.
      return {
        ok: false,
        error: (err as { message?: string })?.message ?? "Connection failed",
        errorCode: null,
        authRequired: false,
        latency_ms: null,
      };
    }
  },

  oauthLogin: async (id: string): Promise<MCPOAuthInit> => {
    const response = await ragApi.post<MCPOAuthInit>(
      `/agent-tools/mcp-connections/${id}/oauth-login`,
    );
    return response.data;
  },

  listMCPTools: async (id: string): Promise<MCPConnectionTool[]> => {
    const response = await ragApi.get<MCPConnectionToolDTO[]>(
      `/agent-tools/mcp-connections/${id}/tools`,
    );
    const items = Array.isArray(response.data) ? response.data : [];
    return items
      .filter(
        (t): t is MCPConnectionToolDTO =>
          !!t && typeof t.name === "string" && !t.is_deleted,
      )
      .map((t) => ({
        id: t.id ?? t.name,
        name: t.name,
        title: t.title ?? "",
        description: t.description ?? "",
        // Preserve the raw type ("read" | "write" | "delete" | ...) so the UI
        // can decide how to bucket it.
        type: (t.type ?? "").toLowerCase(),
        sharing: (t.sharing ?? { scope: "inherit" }) as MCPToolSharing,
      }));
  },

  updateMCPToolSharing: async (
    connectionId: string,
    toolId: string,
    sharing: MCPToolSharing,
  ): Promise<void> => {
    await ragApi.put(
      `/agent-tools/mcp-connections/${connectionId}/tools/${toolId}/sharing`,
      sharing,
    );
  },
};

type MCPConnectionToolDTO = {
  id?: string;
  name: string;
  title?: string | null;
  description?: string | null;
  type?: string | null;
  is_deleted?: boolean;
  input_schema?: unknown;
  sharing?: MCPToolSharing;
};

type MCPConnectionTestErrorDTO = {
  code?: string | null;
  message?: string | null;
};

type MCPConnectionTestResultDTO = {
  ok?: boolean;
  success?: boolean;
  status?: "ok" | "error";
  error?: string | MCPConnectionTestErrorDTO | null;
  message?: string | null;
  latency_ms?: number | null;
};

export interface MCPConnectionTool {
  id: string;
  name: string;
  title: string;
  description: string;
  /** Raw type from the API, lowercased: "read" | "write" | "delete" | ... */
  type: string;
  sharing: MCPToolSharing;
}

export interface MCPConnectionTestResult {
  ok: boolean;
  error: string | null;
  errorCode: string | null;
  /** True when the failure is an expired/missing OAuth token (re-auth needed). */
  authRequired: boolean;
  latency_ms: number | null;
}
